import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const worldHistoryUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Transoceanic Interconnections (c. 1450 – c. 1750)",
  examWeight: "12–15%",
  focus: "How did the establishment of transoceanic connections transform trade, society, the environment, and power structures globally?",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Technological Innovations from 1450 to 1750",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔬 Diffusion of Knowledge</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              European exploration was not an independent invention; it relied on knowledge developed in the Classical, Islamic, and Asian worlds.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">📚 Greek/Roman Influence</h4>
                <p className="text-sm text-slate-700 mt-1">Recovery of geography (Ptolemy) and astronomical charts.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">☪️ Islamic Influence</h4>
                <p className="text-sm text-slate-700 mt-1">Mathematics, improvements to the astrolabe, and preservation of Greek learning.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🏯 Asian Influence</h4>
                <p className="text-sm text-slate-700 mt-1">Gunpowder, compass, and papermaking.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧭 Navigational Technologies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To cross open oceans (the Atlantic/Pacific) rather than hugging coastlines, Europeans adopted:
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Lateen Sail" 
                definition="Triangular sail (originally Arab/Malay) allowed ships to tack against the wind."
              />
              <KeyTerm 
                term="Sternpost Rudder" 
                definition="(Chinese) Allowed for better steering and maneuverability."
              />
              <KeyTerm 
                term="Astrolabe & Magnetic Compass" 
                definition="Allowed sailors to determine latitude and direction."
              />
              <KeyTerm 
                term="Astronomical Charts" 
                definition="Maps of the stars used for navigation."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛵ Ship Designs</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              New vessel types prioritized capacity (cargo) and speed.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🚢 Caravel (Portugal)</h4>
                <p className="text-sm text-slate-700 mt-1">Small, highly maneuverable, used lateen sails. Ideal for exploring shallow rivers and coastlines.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-l-4 border-teal-400">
                <h4 className="font-bold text-teal-900">🚢 Carrack (Portugal)</h4>
                <p className="text-sm text-slate-700 mt-1">Larger vessel used for oceanic travel and trade.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">🚢 Fluyt (Dutch)</h4>
                <p className="text-sm text-slate-700 mt-1">Designed specifically for trade (not war). It had a large cargo hold and required a smaller crew, making Dutch shipping cheaper and more competitive.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 Understanding the Environment</h3>
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200">
              <h4 className="font-bold text-cyan-900">🌀 Volta do Mar</h4>
              <p className="text-sm text-slate-700 mt-1">Portuguese sailors learned the circular wind patterns (gyres) of the Atlantic. They realized they had to sail west into the open ocean to catch winds that would blow them back east/south around Africa.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Exploration: Causes and Events",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👑 State Sponsorship</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Exploration was too expensive for individuals; monarchs funded voyages to increase taxable wealth and prestige.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Mercantilism" 
                definition="The dominant economic theory. States measured wealth by gold/silver accumulation. The goal was a favorable balance of trade (exports > imports). Colonies provided raw materials and markets."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇵🇹 Portuguese Exploration (Indian Ocean focus)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">👨‍🔬 Prince Henry the Navigator</h4>
                <p className="text-sm text-slate-700 mt-1">Established a navigation school; funded exploration of the West African coast.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🗺️ Bartolomeu Dias</h4>
                <p className="text-sm text-slate-700 mt-1">Rounded the Cape of Good Hope (1488).</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌏 Vasco da Gama</h4>
                <p className="text-sm text-slate-700 mt-1">Reached India (1498), establishing a direct maritime route to Asian spice markets, bypassing Ottoman/Venetian middlemen.</p>
              </div>
              <KeyTerm 
                term="Trading Post Empire" 
                definition="Portuguese strategy: They controlled shipping routes and ports (e.g., Goa, Malacca, Hormuz) rather than large territories inland."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇪🇸 Spanish Exploration (Atlantic focus)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🧭 Christopher Columbus</h4>
                <p className="text-sm text-slate-700 mt-1">Sponsored by Isabella and Ferdinand. Sought a western route to Asia; "discovered" the Americas (1492), linking the hemispheres.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">🌍 Ferdinand Magellan</h4>
                <p className="text-sm text-slate-700 mt-1">His crew circumnavigated the globe (he died in the Philippines).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-pink-900">🏝️ Manila Galleons</h4>
                <p className="text-sm text-slate-700 mt-1">Spain conquered the Philippines (Manila) as a commercial bridge between American silver (Potosí) and Chinese markets.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧊 Northern Atlantic Crossings</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              England, France, and the Netherlands sought a Northwest Passage to Asia.
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🇫🇷 French</h4>
                <p className="text-xs text-slate-700 mt-1">Explored the St. Lawrence River and Canada. Focused on the fur trade; generally better relations with Indigenous peoples.</p>
              </div>
              <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🇬🇧 English</h4>
                <p className="text-xs text-slate-700 mt-1">Established settler colonies (Jamestown, 1607). Focused on agriculture and religious freedom.</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🇳🇱 Dutch</h4>
                <p className="text-xs text-slate-700 mt-1">Henry Hudson explored the Hudson River (New Amsterdam/NYC).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: The Columbian Exchange",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦠 Disease (The "Great Dying")</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">
                Indigenous Americans had no immunity to Afro-Eurasian diseases due to geographic isolation.
              </p>
              <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">💀 Smallpox, Measles, Influenza</h4>
                <p className="text-sm text-slate-700 mt-1">Killed 50–90% of native populations.</p>
              </div>
              <ImportantNote>
                Social disruption and labor shortages led Europeans to seek African slave labor.
              </ImportantNote>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌾 Food and Animals</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-bold text-slate-800 mb-3">📤 To the Americas (Old World to New)</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h5 className="font-bold text-yellow-900">🌾 Crops</h5>
                    <p className="text-xs text-slate-700 mt-1">Wheat, sugar, grapes, rice, coffee.</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-100 to-amber-50 p-4 rounded-lg border border-amber-200">
                    <h5 className="font-bold text-amber-900">🐴 Animals</h5>
                    <p className="text-xs text-slate-700 mt-1"><strong>Horses</strong> (transformed Plains Indian culture), pigs, cattle, sheep.</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-3">📥 To Afro-Eurasia (New World to Old)</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900">🥔 Crops</h5>
                    <p className="text-xs text-slate-700 mt-1"><strong>Potatoes</strong> (high calorie, grew in poor soil), <strong>Maize (Corn)</strong>, Manioc (Cassava), Tobacco, Cacao.</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-100 to-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h5 className="font-bold text-emerald-900">📈 Impact</h5>
                    <p className="text-xs text-slate-700 mt-1">Caused massive population growth in Europe, Africa, and China due to better nutrition.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 People</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">⛓️ Trans-Atlantic Slave Trade</h4>
                <p className="text-sm text-slate-700 mt-1">Forced migration of Africans to the Americas to work cash crop plantations (sugar, tobacco, cotton).</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">🚢 European Migration</h4>
                <p className="text-sm text-slate-700 mt-1">Voluntary migration of colonizers seeking land/opportunity.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Maritime Empires Established",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Empire Building in Africa and Asia</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🏰 Trading Posts</h4>
                <p className="text-sm text-slate-700 mt-1">Portuguese (and later Dutch/English) set up forts along the African coast and Indian Ocean.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🤝 African Response</h4>
                <p className="text-sm text-slate-700 mt-1">Some states (Asante, Kingdom of the Kongo) grew wealthy by trading slaves/gold for guns and textiles.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🏯 Asian Isolationism</h4>
                <div className="space-y-2 mt-2">
                  <div className="text-sm text-slate-700">
                    <strong>Ming China:</strong> Restricted trade after Zheng He's voyages; sought to limit European cultural influence.
                  </div>
                  <div className="text-sm text-slate-700">
                    <strong>Tokugawa Japan:</strong> Enacted the <strong>Sakoku</strong> (closed country) policy. Expelled Christians and limited trade to the Dutch at one port (Nagasaki).
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌎 Empire Building in the Americas</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">⚔️ Cortés and Pizarro</h4>
                <p className="text-sm text-slate-700 mt-1">Conquered the Aztecs and Incas using superior tech (guns/steel), disease, and alliances with indigenous enemies.</p>
              </div>
              <KeyTerm 
                term="Treaty of Tordesillas (1494)" 
                definition="Pope divided the world between Spain (West/Americas) and Portugal (East/Brazil + Africa)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛓️ Coerced Labor Systems</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Colonial economies relied on agriculture and mining, requiring cheap labor.
            </p>
            <div className="space-y-3">
              <KeyTerm 
                term="Encomienda" 
                definition="Spanish crown granted settlers the right to force natives to work in exchange for 'protection' and Christian instruction (feudal-like)."
              />
              <KeyTerm 
                term="Hacienda" 
                definition="Large agricultural estates using indigenous labor."
              />
              <KeyTerm 
                term="Mit'a System" 
                definition="Spain adapted the Incan system of labor obligation (corvée) to force natives to work in dangerous silver mines (Potosí)."
              />
              <KeyTerm 
                term="Chattel Slavery" 
                definition="Humans treated as property. Became the dominant system after native populations declined."
              />
              <KeyTerm 
                term="Indentured Servitude" 
                definition="Mostly Europeans working for a set period (7 years) to pay for passage."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Maritime Empires Maintained and Developed",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Commercial Revolution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Shift from a trade-based economy to large-scale production and gold/silver standard.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🏢 Joint-Stock Companies</h4>
                <p className="text-sm text-slate-700 mt-1">Investors pooled money to share risk and profit. These companies acted like governments: they could mint money, make treaties, and raise armies.</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-700"><strong>British East India Company (EIC)</strong></p>
                  <p className="text-xs text-slate-700"><strong>Dutch East India Company (VOC)</strong></p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🪙 The Flow of Silver</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-900">🌍 Global Currency</h4>
                <p className="text-sm text-slate-700 mt-1">Spanish silver from the Americas (Potosí) flowed to Asia (via the Manila Galleons) to purchase silk and porcelain.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🏯 China</h4>
                <p className="text-sm text-slate-700 mt-1">The Ming Dynasty required taxes be paid in silver, causing a massive "silver drain" into China.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Triangular Trade (Atlantic System)</h3>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="bg-blue-300 text-blue-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <p className="text-sm text-slate-700"><strong>Leg 1:</strong> Europe to Africa (Manufactured goods/guns)</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-blue-300 text-blue-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <p className="text-sm text-slate-700"><strong>Leg 2 (Middle Passage):</strong> Africa to Americas (Slaves)</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-blue-300 text-blue-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <p className="text-sm text-slate-700"><strong>Leg 3:</strong> Americas to Europe (Raw materials: Sugar, Tobacco, Rum)</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🙏 Religious Syncretism</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Interaction of cultures led to the mixing of beliefs.
            </p>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🕯️ Vodou (Haiti) & Santería (Cuba)</h4>
                <p className="text-sm text-slate-700 mt-1">Mixed West African spirituality with Catholicism.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">⛪ Cult of Saints</h4>
                <p className="text-sm text-slate-700 mt-1">Indigenous Americans equated Catholic saints with native deities.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Internal and External Challenges to State Power",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔥 Resistance in the Americas</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">🏹 Pueblo Revolt (1680)</h4>
                <p className="text-sm text-slate-700 mt-1">Indigenous groups in modern-day New Mexico rebelled against Spanish missionaries and settlers; successfully drove Spain out for 10 years.</p>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border-l-4 border-red-400">
                <h4 className="font-bold text-red-900">⚔️ Metacom's War (King Philip's War)</h4>
                <p className="text-sm text-slate-700 mt-1">Last major effort by Native Americans in New England to drive out English settlers.</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border-l-4 border-rose-400">
                <h4 className="font-bold text-rose-900">👑 Maroon Societies</h4>
                <p className="text-sm text-slate-700 mt-1">Communities of escaped slaves in the Caribbean (Jamaica) and Brazil (Palmares). <strong>Queen Nanny</strong> led Maroons in Jamaica.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ Resistance in Africa</h3>
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-bold text-yellow-900">👸 Ana Nzinga (Ndongo/Matamba)</h4>
              <p className="text-sm text-slate-700 mt-1">Fought against the Portuguese. She allied with the Dutch and incited rebellion to protect her kingdom from slave raids.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚔️ Resistance in Eurasia</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">🇫🇷 Fronde (France)</h4>
                <p className="text-sm text-slate-700 mt-1">Noble rebellions against the centralization of Louis XIV.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">🇷🇺 Cossack Rebellions (Russia)</h4>
                <p className="text-sm text-slate-700 mt-1">Peasants/warriors (Cossacks) rebelled against serfdom and Catherine the Great (Pugachev Rebellion).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-pink-900">🇮🇳 Maratha Conflict (India)</h4>
                <p className="text-sm text-slate-700 mt-1">Hindu warriors fought against the Mughal Empire, eventually ending Mughal dominance.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Changing Social Hierarchies",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ The Casta System (Latin America)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Spain created a legal hierarchy based on race and ancestry.
            </p>
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="bg-orange-300 text-orange-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <p className="text-sm text-slate-700"><strong>Peninsulares:</strong> Born in Spain (top jobs)</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-orange-300 text-orange-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <p className="text-sm text-slate-700"><strong>Creoles:</strong> Spanish descent born in the Americas</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-orange-300 text-orange-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <p className="text-sm text-slate-700"><strong>Mestizos:</strong> Mixed European/Indigenous</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-orange-300 text-orange-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">4</span>
                  <p className="text-sm text-slate-700"><strong>Mulattoes:</strong> Mixed European/African</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-orange-300 text-orange-900 font-bold rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">5</span>
                  <p className="text-sm text-slate-700"><strong>Indigenous/Africans:</strong> Bottom of the hierarchy</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🕌 Ottoman Society</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">⚔️ Warrior Aristocracy</h4>
                <p className="text-sm text-slate-700 mt-1">Competed with Ulama (scholars) for power.</p>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">🎖️ Janissaries</h4>
                <p className="text-sm text-slate-700 mt-1">Christian boys taken (Devshirme system), converted to Islam, and trained as elite soldiers/bureaucrats. They gained significant political power, sometimes challenging the Sultan.</p>
              </div>
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">👨‍💼 Viziers</h4>
                <p className="text-sm text-slate-700 mt-1">Advisors who gained power as Sultans became less effective.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">☪️ Religious Tolerance</h4>
                <p className="text-sm text-slate-700 mt-1">Relative tolerance toward Jews (expelled from Spain) and Christians, though they paid the <strong>Jizya</strong> tax.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏯 Qing China (Manchu)</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">👥 Manchu vs. Han</h4>
                <p className="text-sm text-slate-700 mt-1">The Manchu minority ruled the Han majority.</p>
              </div>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-pink-900">💇 Queues</h4>
                <p className="text-sm text-slate-700 mt-1">Forced Han men to wear the braided hairstyle as a sign of submission.</p>
              </div>
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-purple-900">📚 Civil Service Exam</h4>
                <p className="text-sm text-slate-700 mt-1">Maintained by the Qing, allowing some Han social mobility, but high positions were reserved for Manchus.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏰 European Nobility</h3>
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700">Nobles struggled for power against absolute monarchs (e.g., Louis XIV in France kept nobles at Versailles to watch them).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-8",
      title: "Topic 4.8: Continuity and Change from 1450 to 1750",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Economic Changes</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">🌍 Global Trade Network</h4>
                <p className="text-sm text-slate-700 mt-1">Creation of the first truly global trade network.</p>
              </div>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">🌊 Atlantic Dominance</h4>
                <p className="text-sm text-slate-700 mt-1">Shift from Mediterranean trade dominance to Atlantic trade dominance.</p>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-200">
                <h4 className="font-bold text-teal-900">💼 Mercantilism & Joint-Stock Companies</h4>
                <p className="text-sm text-slate-700 mt-1">Rise of mercantilism and joint-stock companies.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Economic Continuities</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-bold text-yellow-900">🌾 Agriculture</h4>
                <p className="text-sm text-slate-700 mt-1">Agriculture remained the primary economy for most of the world.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-orange-900">🛣️ Regional Trade Networks</h4>
                <p className="text-sm text-slate-700 mt-1">Traditional regional trade networks (Indian Ocean, Trans-Saharan) continued to flourish despite European entry.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛓️ Labor Systems</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-red-100 to-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">📊 Change</h4>
                <p className="text-sm text-slate-700 mt-1">Massive expansion of chattel slavery and introduction of the encomienda/hacienda systems.</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">📊 Continuity</h4>
                <p className="text-sm text-slate-700 mt-1">Use of traditional peasant labor and serfdom (Russia).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Social Structures</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">📊 Change</h4>
                <p className="text-sm text-slate-700 mt-1">New racial hierarchies (Castas) in the Americas.</p>
              </div>
              <div className="bg-gradient-to-br from-pink-100 to-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">📊 Continuity</h4>
                <p className="text-sm text-slate-700 mt-1">Patriarchal structures persisted globally.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default worldHistoryUnit4Notes;